import { BackgroundCheckColumns } from '@/backend/features/background-check/shared';
import { BackgroundCheckStatus, BackgroundCheckType } from '@/backend/features/background-check/types';
import { requireManageMembersAndAnySystemPermission } from '@/backend/utils';
import { BackgroundCheckTypeSelectModal } from '@/ui/features/background-check/components/background-check-modal/BackgroundCheckTypeSelectModal';
import { SlideOutMenuItemWithId } from '@/ui/hooks';
import { showConfirmDialog, showCustomDialog, showErrorDialog } from '@juvare/common-web-ui/DialogHelper';
import { AccountColumns, AccountData, SystemStatus, hasLowerSystemRole } from '@juvare/contact-management/backend/features/accounts/shared';
import { useUserState } from '@juvare/contact-management/ui/hooks';
import { getSettings } from '@juvare/platform-sdk-react/ui/features/editable-settings';
import { GetResponse, Row } from '@juvare/platform-shared/features/storage';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { BackgroundCheckModal } from '../components/background-check-modal';

export function useRequestBackgroundCheckDetails(): {
    handleRequestBackgroundCheck: (
        account: AccountData,
        backgroundCheck: BackgroundCheckColumns,
        accountId: string,
        reloadAccounts?: () => void,
        onClose?: () => void,
    ) => void;
    handlePreRequestBackgroundCheck: (
        account: AccountData,
        backgroundChecks: Row<BackgroundCheckColumns>[],
        accountId: string,
        reloadAccounts?: () => void,
    ) => void;
    isAllowedToRequestBackgroundCheck: (systemStatus: SystemStatus, isBackgroundCheckTypeEnabled: boolean) => boolean;
    getBackgroundCheckMenuItems: (
        { data, id }: GetResponse<AccountData, AccountColumns>,
        backgroundChecks?: Row<BackgroundCheckColumns>[],
        reloadAccounts?: () => void,
    ) => SlideOutMenuItemWithId[];
} {
    const { backgroundCheckConfig, systemRoleMap } = useSelector(getSettings);
    const { t } = useTranslation();
    const userState = useUserState();
    const navigate = useNavigate();

    const canRequestBackgroundCheck = requireManageMembersAndAnySystemPermission(userState, 'request-background-check');

    const handleReviewProfile = React.useCallback(
        (accountId: string) => {
            navigate(`/accounts/${accountId}?tab=backgroundcheck`);
        },
        [navigate],
    );

    const isAllowedToRequestBackgroundCheck = React.useCallback(
        (systemStatus: SystemStatus, isBackgroundCheckTypeEnabled: boolean) => {
            const isAccountActive = systemStatus === 'active';

            return canRequestBackgroundCheck && isBackgroundCheckTypeEnabled && isAccountActive;
        },
        [canRequestBackgroundCheck],
    );

    const handleRequestBackgroundCheck = React.useCallback(
        (
            account: AccountData,
            backgroundCheck: BackgroundCheckColumns,
            accountId: string,
            reloadAccounts?: () => void,
            onClose?: () => void,
        ) => {
            const isConsentGiven = Boolean(backgroundCheck.consent);
            const notManualBackgroundCheck =
                backgroundCheckConfig.type[backgroundCheck.type as BackgroundCheckType].process === 'automatic';

            const reviewProfileStatuses: BackgroundCheckStatus[] = [
                'completeDenied',
                'invalidSsn',
                'missingFields',
                'processingError',
                'processingNeedsAttention',
                'reviewAndTakeAction',
                'unknown',
            ];
            const backgroundCheckStatus = backgroundCheck.status as BackgroundCheckStatus;

            const isReviewProfile = backgroundCheckStatus && reviewProfileStatuses.includes(backgroundCheckStatus);

            if (!isConsentGiven && notManualBackgroundCheck) {
                return showErrorDialog({ title: t('backgroundCheck.messages.userDidNotConsent') });
            }

            if (hasLowerSystemRole(userState.systemRole, account.systemRole, systemRoleMap)) {
                return showErrorDialog({ title: t('backgroundCheck.messages.cannotPerformBackgroundCheck') });
            }

            if (isReviewProfile) {
                return showConfirmDialog({
                    title: t('backgroundCheck.messages.unableToProcessBackgroundCheck'),
                    content: t('backgroundCheck.messages.cannotBeCompleted'),
                    onConfirm: () => {
                        handleReviewProfile(accountId);
                    },
                    confirmLabel: t('backgroundCheck.labels.reviewProfile'),
                });
            }

            showCustomDialog({
                content: (close, ariaTitleId, ariaContentId) => (
                    <BackgroundCheckModal
                        account={account}
                        backgroundCheck={backgroundCheck}
                        accountId={accountId}
                        ariaContentId={ariaContentId}
                        ariaTitleId={ariaTitleId}
                        onCloseModal={() => {
                            close();

                            if (onClose) {
                                onClose();
                            }
                        }}
                        onReloadAccounts={reloadAccounts}
                    />
                ),
            });
        },
        [handleReviewProfile, systemRoleMap, t, userState.systemRole],
    );

    const handlePreRequestBackgroundCheck = React.useCallback(
        (account: AccountData, backgroundChecks: Row<BackgroundCheckColumns>[], accountId: string, reloadAccounts?: () => void) => {
            showCustomDialog({
                content: (close, ariaTitleId, ariaContentId) => (
                    <BackgroundCheckTypeSelectModal
                        account={account}
                        backgroundCheckData={backgroundChecks}
                        accountId={accountId}
                        ariaContentId={ariaContentId}
                        ariaTitleId={ariaTitleId}
                        onCloseModal={close}
                        handleRequestBackgroundCheck={handleRequestBackgroundCheck}
                        onReloadAccounts={reloadAccounts}
                    />
                ),
            });
        },
        [handleReviewProfile, systemRoleMap, t, userState.systemRole],
    );

    const getBackgroundCheckMenuItems = React.useCallback(
        (
            { data, id }: GetResponse<AccountData, AccountColumns>,
            backgroundChecks?: Row<BackgroundCheckColumns>[],
            reloadAccounts?: () => void,
        ): SlideOutMenuItemWithId[] => {
            const items: SlideOutMenuItemWithId[] = [];

            if (!backgroundChecks) {
                return items;
            }

            if (isAllowedToRequestBackgroundCheck(data.systemStatus, !!backgroundChecks?.length)) {
                if (backgroundChecks.length === 1) {
                    items.push({
                        id: 'menu-item-background-check',
                        label: t('menuItems.backgroundCheck'),
                        onClick: () => handleRequestBackgroundCheck(data, backgroundChecks[0].columns, id, reloadAccounts),
                    });
                } else {
                    items.push({
                        id: 'menu-item-background-check',
                        label: t('menuItems.backgroundCheck'),
                        onClick: () => handlePreRequestBackgroundCheck(data, backgroundChecks, id, reloadAccounts),
                    });
                }
            }

            return items;
        },
        [isAllowedToRequestBackgroundCheck, handleRequestBackgroundCheck, handlePreRequestBackgroundCheck, t],
    );

    return {
        getBackgroundCheckMenuItems,
        isAllowedToRequestBackgroundCheck,
        handleRequestBackgroundCheck,
        handlePreRequestBackgroundCheck,
    };
}
